import logError from '@/utils/logError/logError';
import offeringService from '@/modules/purchasable/services/offeringService';
import businessService from '../services/businessService';

const state = {
  smsBalance: null,
  smsAutoReload: null,
  smsQuotas: null,
  isSmsAvailable: false,
  smsAvailabilityLoaded: false,
};

const actions = {
  async loadSmsBalance({ commit }, businessId) {
    try {
      const balance = await businessService.fetchSmsBalance(businessId);
      if (balance !== null && typeof balance === 'object') {
        commit('setSmsBalance', balance.data);
      }
    } catch (error) {
      logError('There was an error retrieving SMS balance', error);
      commit('setSmsBalance', null);
    }
  },

  async loadSmsAvailability({
    commit, state: currentState, dispatch, rootGetters,
  }, { force = false } = {}) {
    if (currentState.smsAvailabilityLoaded && !force) {
      return;
    }

    if (!rootGetters['BusinessStore/hasFeature']('sms_enabled')) {
      commit('setSmsAvailable', false);
      return;
    }

    // Resolves isSmsPurchaseDisabled, which decides purchase vs upsell when the user clicks.
    try {
      await dispatch('SubscriptionStore/fetchSubscription', null, { root: true });
      await dispatch('SubscriptionStore/loadCurrentBusinessPackage', null, { root: true });
    } catch (error) {
      logError('Failed to resolve the business package for SMS purchase', error);
    }

    try {
      commit('setSmsAvailable', !!await offeringService.fetchSmsOffering());
    } catch (error) {
      logError('Failed to fetch the SMS offering', error);
    }
  },

  async loadSmsAutoReload({ commit, state: currentState }, { businessId, force = false }) {
    if (currentState.smsAutoReload !== null && !force) {
      return;
    }

    try {
      const response = await businessService.fetchSmsAutoReload(businessId);
      commit('setSmsAutoReload', response?.data?.auto_reload === true);
    } catch (error) {
      logError('There was an error retrieving the SMS auto reload setting', error);
    }
  },

  async loadSmsQuotas({ commit, state: currentState }, { businessId, force = false }) {
    if (currentState.smsQuotas !== null && !force) {
      return;
    }

    try {
      const quotas = (await businessService.fetchSmsQuotas(businessId))?.data || null;
      commit('setSmsQuotas', quotas);
      if (quotas) {
        commit('setSmsAutoReload', quotas.sms_auto_reload === true);
      }
    } catch (error) {
      logError('There was an error retrieving the SMS quotas', error);
    }
  },

  async updateSmsAutoReload({ commit, state: currentState }, { businessId, enabled }) {
    const previous = currentState.smsAutoReload;
    commit('setSmsAutoReload', enabled);

    try {
      const response = await businessService.updateSmsAutoReload(businessId, enabled);
      commit('setSmsAutoReload', response?.data?.auto_reload === true);
    } catch (error) {
      commit('setSmsAutoReload', previous);
      throw error;
    }
  },
};

const mutations = {
  setSmsBalance(state, balance) {
    state.smsBalance = balance;
  },
  setSmsQuotas(state, quotas) {
    state.smsQuotas = quotas;
  },
  setSmsAutoReload(state, enabled) {
    state.smsAutoReload = enabled;
  },
  setSmsAvailable(state, isAvailable) {
    state.isSmsAvailable = isAvailable;
    state.smsAvailabilityLoaded = true;
  },
};

const getters = {
  smsBalance: (state) => state.smsBalance,
  smsQuotas: (state) => state.smsQuotas,
  smsAutoReload: (state) => state.smsAutoReload === true,
  isSmsAvailable: (state) => state.isSmsAvailable,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
